/**
 * The Studio's LIVE measured-fit gate (spec 2026-09-17 §3.4, second row).
 *
 * After ANY journal change (load, commit, Undo, Reset — StudioEditor runs it
 * as a derived effect), the same pure gate the server runs
 * (`src/lib/measuredFit.ts`) decides which cues no longer fit the design in
 * force and yields ONE journal entry of cueSplits — visible in History,
 * undoable, and exactly what the render will bake. The browser measures with the canvas: the Player is already
 * drawing with these faces. When the primary face is not loaded yet the
 * gate skips rather than guess (translate-time fit and the render safety
 * net still stand behind it).
 */
import { fitCuesToDesign } from "../../../src/lib/measuredFit.ts";
import type { MeasureFor } from "../../../src/lib/captionRows.ts";
import { baselineBrand, baselineLayout, type EditJournalEntry } from "../../../src/lib/clipEditState.ts";
import { createCanvasMeasurer, letterSpacingToPx } from "../../../src/lib/textMeasure.ts";
import type { ClipProps, RenderBrand, RenderLayout } from "../../../src/remotion/types.ts";

/** Canvas measurers for the clip's faces, or null when the primary caption
 *  face isn't loaded (createCanvasMeasurer's own fonts gate). Mirrors
 *  CaptionLine's measFor: uniform tracking, uppercase measured uppercased. */
export function browserMeasureFor(props: ClipProps): MeasureFor | null {
  const cap = baselineLayout(props).caption;
  const brand = baselineBrand(props);
  const spacing = letterSpacingToPx(cap.letterSpacing);
  const upper = cap.textTransform === "uppercase";
  const probe = createCanvasMeasurer({
    fontFamily: `"${brand.captionFont}", sans-serif`,
    fontWeight: 700,
    fontSizePx: cap.fontSizePx,
    letterSpacingPx: spacing,
  });
  if (!probe) return null;
  return (family, weight, sizePx) => {
    const m = createCanvasMeasurer({
      fontFamily: `"${family}", sans-serif`,
      fontWeight: weight,
      fontSizePx: sizePx,
      letterSpacingPx: spacing,
    });
    // An unloaded secondary face measures as nothing: never split on a guess.
    if (!m) return () => 0;
    return upper ? (t) => m(t.toLocaleUpperCase()) : m;
  };
}

export function fitEntryForDesignCommit(args: {
  props: ClipProps;
  measFor: MeasureFor;
  now: number;
}): EditJournalEntry | null {
  const cap = baselineLayout(args.props).caption as RenderLayout["caption"];
  const brand = baselineBrand(args.props) as RenderBrand;
  const out = fitCuesToDesign(args.props.cues, cap, brand, args.measFor);
  if (out.splits.length === 0) return null;
  const n = out.splits.length;
  const lines = cap.maxLines ?? 2;
  return {
    ts: args.now,
    source: "fit",
    label: `Split ${n} caption${n === 1 ? "" : "s"} to fit ${lines} line${lines === 1 ? "" : "s"} at ${cap.fontSizePx}px`,
    patch: { cueSplits: out.splits },
  };
}
